import {
  Breadcrumb,
  BreadcrumbSection,
  Button,
} from "@screencloud/screencloud-ui-components";
import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--color-background, #fff);
  border-radius: 8px;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: var(--spacing-lg, 16px) var(--spacing-xl, 24px);
  border-bottom: 1px solid var(--color-border, #c5c6ca);
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 900;
  line-height: 1.75rem;
  color: var(--color-gray-950, #19191b);
`;

/* Holds the preview breadcrumb at Title's height so the header doesn't shift
   when one replaces the other; the breadcrumb's own 32px min-height (4px more
   than Title) centres inside it instead of growing the header. */
export const TitleSlot = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
  height: 1.75rem;
  margin-right: var(--spacing-md, 12px);
`;

/* Same type scale as Title; sections inherit it from the breadcrumb root. */
export const TitleBreadcrumb = styled(Breadcrumb)`
  && {
    min-width: 0;
    max-width: 100%;
    font-size: 1.25rem;
    font-weight: 900;
    line-height: 1.75rem;
  }
`;

/* Only the file name truncates; the way back stays fully readable. */
export const ParentCrumb = styled(BreadcrumbSection)`
  && {
    flex-shrink: 0;
    white-space: nowrap;
  }
`;

/* The current crumb is a plain span rather than a BreadcrumbSection: the
   library caps sections at 200px and clips them without an ellipsis, which
   hides most of a long file name. This one uses the free space instead. */
export const CurrentCrumb = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-gray-950, #19191b);
`;

export const CloseButton = styled(Button)`
  && {
    flex-shrink: 0;
  }
`;

/* Non-scrolling shell: each column owns its scroll state independently
   (GRWBKK-1767), so finding or reading one side never moves the other. */
/* Hosts the comparison and, on top of it, an item preview. The comparison
   stays mounted (visibility, not display) so column scroll positions and the
   find highlight survive a round trip through the preview. */
export const BodyFrame = styled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`;

export const Body = styled.div<{ $hidden?: boolean }>`
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: var(--color-gray-50, #f6f6f7);
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-sm, 8px);
  flex-shrink: 0;
  padding: var(--spacing-lg, 16px) var(--spacing-xl, 24px);
  border-top: 1px solid var(--color-border, #c5c6ca);
`;

export const PreviewPane = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--color-gray-50, #f6f6f7);
  outline: none;
`;

export const PreviewContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  padding: var(--spacing-xl, 24px);
`;
